import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";

export const AdministratorMyShortedEventsMarkedOnHand = () => {
    const dispatch = useDispatch();

    const [myShortedEventsMarkedOnHand, setMyShortedEventsMarkedOnHand] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedTicketGroupId, setLastSelectedTicketGroupId] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 50,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };

    const getMyShortedEventsMarkedOnHand = () => {
        setIsLoading(true);

        const dataItem = {

        };

        fetch(`/api/administrator/pos/myshortedeventsmarkedonhand`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setPage(initialPageState);
                setMyShortedEventsMarkedOnHand(result);
                setTotalEventCount(result.length);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedTicketGroupId(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const markTicketGroupsAsNotOnHand = (ticketGroups) => {
        if (window.confirm("Are you sure you want to mark these ticket groups as NOT on hand?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketGroups: ticketGroups,
        };

        fetch(`/api/administrator/pos/markticketgroupsasnotonhand`,
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Marked ticket groups as NOT on hand successfully");

                setPage(initialPageState);
                getMyShortedEventsMarkedOnHand();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const getGridData = () => {
        return myShortedEventsMarkedOnHand.slice(page.skip, page.take + page.skip);
    };

    const SelectedCell = (props) => {
        const ticketGroupId = props.dataItem.ticketGroupId;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, ticketGroupId, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const TagsCell = (props) => {
        const tags = props.dataItem.tags;

        return (
            <td className={"text-nowrap"}>
                <div className={"tag-list"}>
                    {tags.map((tag, index) => {
                        return <span className={"tag-list-item"}>{tag}</span>;
                    })}
                </div>
            </td>
        );
    };

    const selectedValueChanged = (event, ticketGroupId, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let myShortedEventsMarkedOnHandCopy = myShortedEventsMarkedOnHand;

            const itemIndex = myShortedEventsMarkedOnHandCopy.findIndex(dataItem =>
                dataItem.ticketGroupId === ticketGroupId
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedTicketGroupId !== undefined
                && lastSelectedTicketGroupId !== ticketGroupId) {
                const lastSelectedItemIndex = myShortedEventsMarkedOnHandCopy.findIndex(dataItem =>
                    dataItem.ticketGroupId === lastSelectedTicketGroupId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        myShortedEventsMarkedOnHandCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                myShortedEventsMarkedOnHandCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedTicketGroupId(ticketGroupId);
            }

            setMyShortedEventsMarkedOnHand(myShortedEventsMarkedOnHandCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let myShortedEventsMarkedOnHandCopy = myShortedEventsMarkedOnHand;
            
            myShortedEventsMarkedOnHandCopy.forEach(dataItem =>
                dataItem.selected = false
            );

            if (checked === true) {
                const gridData = getGridData();

                myShortedEventsMarkedOnHandCopy.forEach(dataItem => {
                    const exists = gridData.includes(dataItem);

                    if (exists) {
                        dataItem.selected = true
                    }
                });
            }      

            setMyShortedEventsMarkedOnHand(myShortedEventsMarkedOnHandCopy);

            setLastSelectedTicketGroupId(undefined);

            setIsLoading(false);
        }, 1);
    };

    const getSelectedTicketGroups = () => {
        let ticketGroups = [];

        myShortedEventsMarkedOnHand.forEach(dataItem => {
            if (dataItem.selected === true) {
                ticketGroups.push({
                    ticketGroupId: dataItem.ticketGroupId,
                    eventDate: dataItem.eventDate,
                });
            }
        });

        return ticketGroups;
    }

    const markSelectedTicketGroupsAsNotOnHand = () => {
        let ticketGroups = getSelectedTicketGroups();

        if (ticketGroups.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No ticket groups selected");
            return;
        }

        markTicketGroupsAsNotOnHand(ticketGroups);
    };

    useEffect(() => {
        document.title = "My Shorted Events Marked On Hand";

        getMyShortedEventsMarkedOnHand();
    }, []);

    return (
        <div className="administratorReport">            
            {isLoading ? <LoadingPanel /> : <></>}

            {!myShortedEventsMarkedOnHand ? <></> :
                <>
                    <div className="mt-3 mb-3 ms-2 d-flex flex-row">
                        <div>
                            <Button type={"button"} icon="edit" title="Mark Selected Ticket Groups as NOT On Hand" onClick={markSelectedTicketGroupsAsNotOnHand}>
                                Mark Selected Ticket Groups as NOT On Hand
                            </Button>
                        </div>
                    </div>

                    <Grid
                        data={getGridData()}
                        resizable={true}
                        skip={page.skip}
                        take={page.take}
                        total={totalEventCount}
                        pageable={true}
                        onPageChange={pageChange}
                        scrollable="none"
                    >
                        <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                        <Column field="eventName" title={"Event"} />
                        <Column field="venueName" title={"Venue"} />
                        <Column field="eventDate" title={"Date"} />
                        <Column field="isBroadcasted" title={"Broadcasted"} />
                        <Column field="quantity" title={"Quantity"} />
                        <Column field="price" title={"Price"} />
                        <Column field="cost" title={"Cost"} />
                        <Column cell={TagsCell} title={"Tags"} />                        
                    </Grid>
                </>
            }
        </div>
    );
}